import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useBranding } from "../hooks/useBranding";
import { Button } from "./ui/button";
import { LogOut, ArrowLeft } from "lucide-react";

export const Header = ({ title }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Phase 2 P2 #5b — pull the operator's company name from the shared
  // branding cache. Falls back to "Crystal Dispatch" if the public
  // endpoint is unreachable. Visual treatment (uppercase wordmark +
  // yellow period) is preserved regardless of operator length.
  const brand = useBranding();
  const wordmark = (brand?.company_name || "Crystal Dispatch").toUpperCase();

  const handleLogout = async () => {
    const result = await logout();
    if (result?.blocked) return; // backend refused — alert already shown
    navigate("/");
  };

  // Active-trip recovery (production hotfix · 2026-06-XX):
  // Defence-in-depth in case any public/marketing page slips past the
  // `PublicRoute` redirect (App.js). When an authenticated user is on a
  // public route, surface a one-tap "Return to your trip" button so they
  // can never be stranded away from the live dashboard.
  const dashboardFor = (role) =>
    role === "admin" ? "/admin" : role === "driver" ? "/driver" : "/passenger";
  const dashPath = user ? dashboardFor(user.role) : null;
  const onPublicRoute =
    location.pathname === "/" ||
    location.pathname === "/login" ||
    location.pathname === "/register";
  const showResumeButton = !!user && onPublicRoute;

  return (
    <header className="border-b border-border bg-white sticky top-0 z-30" data-testid="app-header">
      <div className="flex items-center justify-between px-4 md:px-8 py-4">
        <Link to="/" className="flex items-center gap-3 group min-w-0" data-testid="logo-link">
          <div className="relative flex-shrink-0">
            {/* Crystal Dispatch icon — the simplified gem variant (no text);
                the wordmark sits to the right so it scales correctly on
                small screens. PNG sourced from /public, served at the
                site root by webpack-dev-server / static hosting.

                Mobile brand-impact pass (2026-06 · UX-only): the gem's green
                + gold accents wash out at 36px on the white header. On small
                screens we render it larger (44px), give it an app-icon radius,
                a defining gold ring, a soft gold glow, and lift the
                brightness/saturation/contrast so the accents read clearly.
                ALL of these reset at `md:` so the desktop wordmark is pixel-
                identical to before — no overall branding change. */}
            <img
              src="/pwa-icon-192.png"
              alt={brand?.company_name || "Crystal Dispatch"}
              className="w-11 h-11 md:w-9 md:h-9 rounded-md md:rounded-sm object-cover
                ring-1 ring-[#FFC107]/40 md:ring-0
                shadow-[0_2px_12px_rgba(255,193,7,0.45)] md:shadow-none
                brightness-110 saturate-125 contrast-105
                md:brightness-100 md:saturate-100 md:contrast-100
                transition-transform duration-200 group-hover:scale-105 group-active:scale-95"
              width={44}
              height={44}
              loading="eager"
              decoding="async"
            />
          </div>
          <div className="flex flex-col leading-none min-w-0">
            <span
              className="font-heading text-xl font-black tracking-tight truncate"
              data-testid="header-brand-name"
              title={brand?.company_name || "Crystal Dispatch"}
            >
              {wordmark}<span className="text-[#FFC107]">.</span>
            </span>
            <span className="label-mini text-muted-foreground mt-0.5">{title || "PRIVATE HIRE · HAMPSHIRE"}</span>
          </div>
        </Link>

        <div className="flex items-center gap-3">
          {showResumeButton && (
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate(dashPath)}
              className="bg-[#FFC107] text-[#0A0A0A] hover:bg-[#FFC107]/90 font-bold"
              data-testid="return-to-trip-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" />
              Return to your trip
            </Button>
          )}
          {user && (
            <div className="flex items-center gap-2 md:gap-3 px-2 md:px-3 py-1 md:py-1.5 border border-border rounded-sm" data-testid="user-chip">
              <div className="flex flex-col items-end leading-tight">
                <span className="text-xs md:text-sm font-semibold" data-testid="user-chip-name">{user.name}</span>
                <span className="label-mini text-muted-foreground hidden md:block">{user.role}</span>
                {/* Mobile-only email line so a driver who left a session
                    open on a friend's phone can spot the wrong account
                    at a glance. */}
                <span className="text-[9px] text-muted-foreground md:hidden" data-testid="user-chip-email">{user.email}</span>
              </div>
              <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center font-bold text-xs md:text-sm">
                {user.name?.[0]?.toUpperCase()}
              </div>
            </div>
          )}
          {user ? (
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-1.5" />
              Sign out
            </Button>
          ) : (
            <>
              <Link to="/login">
                <Button variant="outline" size="sm" data-testid="header-login-btn">Sign in</Button>
              </Link>
              <Link to="/register">
                <Button size="sm" data-testid="header-register-btn">Get started</Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
